/**
 * Option-linked PDP image gallery + Vimeo 3D-view thumbnail.
 *
 * Filters the thumbnail carousel to the images belonging to the selected
 * product option, keyed off a "|| Option Value" token that
 * components/common/responsive-img.html parses out of each image's alt text
 * into a data-option-value attribute.
 *
 * Supersedes the SKU-keyed filter that used to live in common/product-details.js
 * (updateProductGalleryForSku / slickFilterBySku); legacy data-sku matching is
 * preserved here as clause 2 of matchesSelection.
 */

const norm = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * @param {string} url Any Vimeo URL, including the unlisted /{id}/{hash} form.
 * @returns {{id: string, hash: string|null}|null}
 */
export function parseVimeoUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return null;

    const trimmed = url.trim();
    const match = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-zA-Z]+))?/);
    if (!match) return null;

    let hash = match[2] || null;
    if (!hash) {
        const fromQuery = trimmed.match(/[?&]h=([0-9a-zA-Z]+)/);
        if (fromQuery) [, hash] = fromQuery;
    }

    return { id: match[1], hash };
}

/** @returns {string} The player URL, carrying the privacy hash when present. */
export function buildPlayerUrl({ id, hash }) {
    const base = `https://player.vimeo.com/video/${id}`;
    return hash ? `${base}?h=${hash}` : base;
}

/**
 * The unified match rule. A slide is visible when its option token matches a
 * selected label, OR its legacy SKU matches the active variant, OR it carries
 * no marker at all (generic shots and the video thumb).
 */
export function matchesSelection(tokens = {}, state = {}) {
    const optionValue = norm(tokens.optionValue);
    const sku = norm(tokens.sku);

    if (!optionValue && !sku) return true;

    if (optionValue) {
        const labels = state.selectedLabels || [];
        if (labels.some(label => norm(label) === optionValue)) return true;
    }

    if (sku && norm(state.activeSku) === sku) return true;

    return false;
}

/**
 * The product custom-field name that carries a Vimeo URL. An option-scoped
 * video appends a "|| Option Value" token to the field NAME, mirroring the
 * image alt-text convention so the client learns one delimiter, not two.
 */
const VIDEO_FIELD_NAME = '__vimeo-embed';

/**
 * Parses one product custom field into a video descriptor.
 *
 * Recognised forms, in the order an editor is likely to reach for them:
 *
 *   name: "__vimeo-embed"            value: "<url>"              -> product-level
 *   name: "__vimeo-embed || Maple"   value: "<url>"              -> option-scoped
 *   name: "__vimeo-embed"            value: "<url> || Maple"     -> option-scoped
 *
 * The name form is the documented one. The value form is supported as a hedge:
 * we cannot verify from here whether BigCommerce's back office accepts "|" in a
 * custom-field NAME, and if it silently rejects or rewrites it the client would
 * be stuck with no working convention at all. Parsing both costs a few lines
 * and removes that risk. When both carry a token the NAME wins, because that is
 * the form we tell the client to use.
 *
 * Case handling deliberately splits: the field name must match
 * `__vimeo-embed` exactly, preserving the existing case-sensitive comparison
 * this feature shipped with, while the option token is compared case- and
 * whitespace-insensitively like every other option token in this module.
 *
 * @param {{name: string, value: string}} field One entry of product.custom_fields.
 * @returns {{url: string, optionValue: string}|null} null when the field is not
 *   a video field, or carries no URL to render.
 */
export function parseVideoField(field) {
    if (!field || typeof field !== 'object') return null;

    const name = typeof field.name === 'string' ? field.name.trim() : '';
    if (!name.startsWith(VIDEO_FIELD_NAME)) return null;

    // Anything after the field name must be a delimiter, so a differently named
    // field like `__vimeo-embed-notes` is not mistaken for a video.
    const remainder = name.slice(VIDEO_FIELD_NAME.length).trim();
    if (remainder && !remainder.startsWith('|')) return null;

    const nameToken = remainder.includes('||')
        ? remainder.split('||').pop().trim()
        : '';

    // Split the value regardless of where the token came from, so the URL is
    // always clean even when an editor tokenises both name and value.
    const valueParts = (typeof field.value === 'string' ? field.value : '').split('||');
    const url = valueParts[0].trim();
    if (!url) return null;

    const valueToken = valueParts.length > 1 ? valueParts[valueParts.length - 1].trim() : '';

    return { url, optionValue: nameToken || valueToken };
}

/**
 * Chooses the ONE video thumbnail to show for the current selection.
 *
 * Videos are mutually exclusive, unlike photos: an option-scoped video replaces
 * the product-level one rather than appearing alongside it. That is the client's
 * stated model - "one video is shared between all variants" is the thing they
 * asked us to fix - and it keeps the strip to a single play badge, so a shopper
 * never has to guess which of two videos belongs to what they picked.
 *
 * Precedence: a video whose token matches any selected option label, else the
 * untokenised product-level video, else nothing. The middle case is the
 * fallback the option tagging system already uses for photos.
 *
 * @param {Element[]} slides Every video <li> in the carousel.
 * @param {{selectedLabels: string[]}} state
 * @returns {Element|null}
 */
export function selectVideoSlide(slides, state = {}) {
    const list = Array.isArray(slides) ? slides.filter(Boolean) : [];
    if (!list.length) return null;

    const labels = state && Array.isArray(state.selectedLabels) ? state.selectedLabels : [];
    const tokenOf = el => norm((el.getAttribute && el.getAttribute('data-option-value')) || '');

    const matched = list.find(el => {
        const token = tokenOf(el);
        return !!token && labels.some(label => norm(label) === token);
    });

    return matched || list.find(el => !tokenOf(el)) || null;
}

const SLICK_SELECTOR = '#product-details-image-carousel';

/** Reads match tokens off a slide. The video thumb has no tokens and falls through to {}. */
export function readSlideTokens(slide) {
    if (!slide) return { optionValue: '', sku: '' };

    const img = slide.querySelector('img[data-option-value], img[data-sku]');
    const source = img || slide;

    return {
        optionValue: source.getAttribute('data-option-value') || '',
        sku: source.getAttribute('data-sku') || '',
    };
}

/** Labels of every currently selected option value, across radios and dropdowns. */
export function readSelectedLabels(form) {
    if (!form) return [];

    const labels = [];

    form.querySelectorAll('input[type="radio"]:checked[data-option-label]').forEach(input => {
        labels.push(input.getAttribute('data-option-label'));
    });

    form.querySelectorAll('select').forEach(select => {
        const option = select.options[select.selectedIndex];
        if (!option || !option.value) return;

        // Require data-option-label rather than falling back to the option's own
        // text. Two reasons:
        // 1. common/product-details.js's disableSelectOptionAttribute() implements
        //    `label_option` out-of-stock behavior by appending the out-of-stock
        //    message into the option's markup and leaving it there:
        //      $attribute.html($attribute.html().replace(msg, '') + msg)
        //    so on the option change AFTER that runs, textContent reads
        //    "Large (Out of stock)", the "|| Large" image token stops matching and
        //    that option's images silently vanish from the carousel. The
        //    attribute is written once by set-select.html and never mutated.
        // 2. Not every <select> in the form is a variant-driving product option.
        //    options/date.html renders day/month/year selects that can carry a
        //    non-empty, preselected value (e.g. a single-year range auto-selects
        //    its <option>) with no product-option semantics at all. Falling back
        //    to textContent there would feed a junk label like "2026" into the
        //    match set and make hasSelection() report a selection that was never
        //    made by the shopper. data-option-label is only ever rendered by the
        //    variant-driving option templates (swatch, set-rectangle, set-select,
        //    set-radio), so requiring it here doubles as the allowlist.
        const label = option.getAttribute('data-option-label');
        if (label) labels.push(label);
    });

    return labels.filter(Boolean);
}

/**
 * True when this product opts into the feature by tagging at least one image,
 * whether via the new option-value convention or the legacy sku convention.
 * This is the store-wide inertness guarantee: products with neither attribute
 * return early and are never touched by this feature.
 *
 * `root` must be the thumbnail carousel, NOT the whole .productView — see the
 * call site for why. A missing carousel yields a falsy root and a quiet no-op.
 */
export function hasTaggedImages(root) {
    return !!(root && root.querySelector('img[data-option-value], img[data-sku]'));
}

/**
 * True when the shopper has something to filter by: either a selected option
 * label (new convention) or an active variant sku (legacy convention). With
 * neither, there is nothing to filter against, so the caller should leave the
 * carousel unfiltered rather than hiding every tagged slide.
 */
export function hasSelection(state = {}) {
    const hasLabels = Array.isArray(state.selectedLabels) && state.selectedLabels.length > 0;
    const hasActiveSku = typeof state.activeSku === 'string' && state.activeSku.trim().length > 0;
    return hasLabels || hasActiveSku;
}

export const EXCLUDED_ATTR = 'data-gallery-excluded';

/**
 * A slide this feature has taken permanently out of play (currently only a
 * video thumb whose __vimeo-embed value would not parse).
 *
 * Such a slide cannot simply be removed from the DOM. slick snapshots its slide
 * list into `_.$slidesCache` at init and rebuilds the track from that snapshot
 * on every filter — node_modules/slick-carousel/slick/slick.js:993-1008:
 *
 *   _.$slidesCache = _.$slides;
 *   _.$slideTrack.children(this.options.slide).detach();
 *   _.$slidesCache.filter(filter).appendTo(_.$slideTrack);
 *
 * A detached <li> is still in that cached set, and readSlideTokens returns empty
 * tokens for it so matchesSelection would call it untagged-and-always-visible.
 * The next option change would therefore re-insert a dead loading.svg thumbnail
 * with no click handler. Marking the slide instead keeps it inert across every
 * unfilter/refilter cycle.
 */
export function isExcludedSlide(slide) {
    return !!(slide && typeof slide.hasAttribute === 'function' && slide.hasAttribute(EXCLUDED_ATTR));
}

const VIDEO_SLIDE_SELECTOR = '[data-video-thumb]';

/** True for a video thumbnail <li>, which is filtered by selectVideoSlide rather than matchesSelection. */
function isVideoSlide(slide) {
    return !!(slide && typeof slide.matches === 'function' && slide.matches(VIDEO_SLIDE_SELECTOR));
}

/**
 * Puts the video thumbnail SECOND in the strip, behind the first photo the
 * shopper can actually see.
 *
 * Only six thumbnails fit the desktop window, so a video rendered after every
 * photo drops out of view on any product with seven or more visible thumbs -
 * the client hit exactly that. product-view.html already places it second in
 * the delivered markup, which covers first paint; this keeps it second after
 * every option change, when which photos are visible changes.
 *
 * It works WITH slick's filter rather than against it. slickFilter rebuilds the
 * track from `$slidesCache`, which it copies from `$slides` on entry, and
 * jQuery's .filter() preserves collection order - so reordering `$slides` here
 * is enough to choose the post-filter order, and the reinit() that slickFilter
 * runs afterwards re-derives every other internal (slideCount, indices, arrows)
 * from the rebuilt track. Nothing is left stale, which is why this is preferred
 * over moving <li>s in the DOM after the fact.
 *
 * Non-matching slides keep their relative order after the pair; they are about
 * to be filtered out anyway, and preserving them keeps slickUnfilter's restored
 * order stable.
 *
 * Bails quietly if the slick build has no getSlick, or if jQuery is not a
 * global (unit tests import this module without webpack's ProvidePlugin). The
 * cost of bailing is only that the video keeps its server-rendered position.
 */
function reorderCacheForVideo($slick, slides, visible, chosenVideo) {
    const firstVisiblePhoto = visible.find(slide => slide !== chosenVideo);
    if (!firstVisiblePhoto) return;

    let instance = null;
    try {
        instance = $slick.slick('getSlick');
    } catch (error) {
        return;
    }
    if (!instance || !instance.$slides) return;
    if (typeof $ === 'undefined') return;

    const rest = slides.filter(slide => slide !== firstVisiblePhoto && slide !== chosenVideo);
    instance.$slides = $([firstVisiblePhoto, chosenVideo, ...rest]);
}

/**
 * Positional angle memory (client review round 3).
 *
 * The client shoots every variant from the same angles in the same back-office
 * order, so "the angle the shopper was looking at" is simply a POSITION in the
 * visible strip. When the shopper clicks a thumbnail we remember its position
 * and the strip's visible count; when they switch variants we reselect the same
 * position in the new strip. The count is the agreed integrity check: if the
 * new option shows a different number of thumbnails the positions cannot be
 * trusted to be the same angles, and the strip falls back to the first image.
 * That check also covers the video thumb for free - the video sits second in
 * every option that has one, so counts only diverge when one option lacks a
 * video (or has extra/missing photos), which is exactly when positional
 * identity breaks.
 *
 * Keyed by the carousel ELEMENT so the quickview modal's own carousel gets its
 * own memory, mirroring how the $scope/$slick pair already isolates instances.
 */
const galleryMemory = new WeakMap();

/** Records the shopper's selected thumbnail position for one carousel. */
export function rememberGallerySelection(carouselEl, index, count) {
    if (!carouselEl || typeof carouselEl !== 'object') return;
    if (!Number.isInteger(index) || index < 0) return;
    if (!Number.isInteger(count) || count < 1) return;
    galleryMemory.set(carouselEl, { index, count });
}

/** @returns {{index: number, count: number}|null} */
export function getGallerySelection(carouselEl) {
    if (!carouselEl) return null;
    return galleryMemory.get(carouselEl) || null;
}

/**
 * Decides which visible position to land on after a filter.
 *
 * null means "nothing remembered - keep the default behavior" and is
 * deliberately distinct from 0, which is the ACTIVE fallback ("select the
 * first image") the client agreed to for options whose thumbnail counts
 * don't line up.
 */
export function chooseRestoreIndex(memory, visibleCount) {
    if (!memory || typeof memory !== 'object') return null;
    if (memory.count !== visibleCount) return 0;
    if (!Number.isInteger(memory.index) || memory.index < 0 || memory.index >= visibleCount) return 0;
    return memory.index;
}

/**
 * The slides a shopper can currently see, in displayed order. After a
 * slickFilter the non-matching slides are detached from the track entirely, so
 * document order inside the carousel IS the displayed order; the inline
 * display:none check covers the dead-video case, which is hidden rather than
 * detached (see isExcludedSlide for why).
 */
export function getVisibleSlides(carouselEl) {
    if (!carouselEl || typeof carouselEl.querySelectorAll !== 'function') return [];
    return Array.from(carouselEl.querySelectorAll('.slick-slide:not(.slick-cloned)'))
        .filter(slide => !isExcludedSlide(slide) && (!slide.style || slide.style.display !== 'none'));
}

/**
 * Filters the slick thumbnail carousel to the matching slides.
 * Leaves the carousel unfiltered when nothing matches, so it is never empty.
 *
 * @param {{index: number, count: number}|null} [memory] The remembered
 *   thumbnail position (see rememberGallerySelection); the post-filter
 *   slickGoTo lands on it when the visible count still matches.
 */
export function filterGallery($slick, state, memory = null) {
    // No carousel element on this page. That is a legitimate state, not a
    // defect: product-view.html only renders the <ul> when the product has more
    // than one image or a __vimeo-embed field, so a single-image product has
    // nothing to filter. Return quietly — warning here fired on every option
    // change for such products across all six storefronts.
    if (!$slick || !$slick.length) return;

    // The element exists but the slick plugin is not on it. That IS a defect
    // worth surfacing: the thumbnails silently stop filtering.
    if (typeof $slick.slick !== 'function') {
        console.warn(`[option-image-gallery] slick not initialized at ${SLICK_SELECTOR}`);
        return;
    }

    $slick.slick('slickUnfilter');

    // Neither a selected option label nor an active sku means there is nothing
    // to filter by (e.g. a required option that hasn't been chosen yet on
    // initial load) — stay explicitly unfiltered instead of matching only
    // untagged slides.
    if (!hasSelection(state)) {
        $slick.slick('setPosition');
        return;
    }

    const slides = $slick.find('.slick-slide:not(.slick-cloned)').toArray();
    // Excluded slides are already hidden inline and must never be counted as
    // matches, or the "everything matches, nothing to do" shortcut below would
    // be decided by a slide the shopper cannot see.
    const eligible = slides.filter(slide => !isExcludedSlide(slide));

    // Videos are mutually exclusive: an option-scoped video REPLACES the
    // product-level one instead of showing beside it, so the strip never offers
    // a shopper two play badges to choose between. They therefore cannot go
    // through matchesSelection, whose rule 3 would keep the untokenised
    // product-level video visible alongside a matching option video.
    const chosenVideo = selectVideoSlide(eligible.filter(isVideoSlide), state);
    const shows = slide => (isVideoSlide(slide)
        ? slide === chosenVideo
        : matchesSelection(readSlideTokens(slide), state));

    const visible = eligible.filter(shows);

    // Zero matches would empty the carousel; a full match set means no work to
    // do - and in that case the server-rendered order already has the video
    // second, so skipping the reorder below costs nothing.
    if (!visible.length || visible.length === eligible.length) {
        $slick.slick('setPosition');
        return;
    }

    if (chosenVideo) reorderCacheForVideo($slick, slides, visible, chosenVideo);

    $slick.slick('slickFilter', function filterPredicate() {
        return !isExcludedSlide(this) && shows(this);
    });

    // variableWidth + vertical mode leave stale metrics after a filter.
    $slick.slick('setPosition');
    $slick.slick('slickGoTo', chooseRestoreIndex(memory, visible.length) ?? 0, true);
}

const SCOPE_SELECTOR = '.productView';
const FORM_SELECTOR = 'form[data-cart-item-add]';

/**
 * Marker class added to the thumbnail <ul> only for products where this
 * feature actually activates (hasTaggedImages() is true). Lets
 * custom/pages/_product.scss scope the uniform-crop thumbnail styling
 * (Fix 3) to exactly those products instead of every storefront/product that
 * shares .productView-thumbnails.
 */
export const FILTERED_CLASS = 'is-option-filtered';

/**
 * Hook entry point, called by common/product-details.js on initial load and on
 * every option change. Returns immediately on products that use neither
 * recognised convention — the "||" delimiter (data-option-value) nor the
 * legacy "SKU|alt" delimiter (data-sku) — which is what keeps the feature
 * inert store-wide.
 *
 * @param {Object} data productAttributes payload; data.sku is the active variant SKU.
 * @param {jQuery} [$scope] The calling ProductDetails instance's `.productView`
 *   root (its `this.$scope`). Scopes the lookup to that instance so a quickview
 *   modal's own ProductDetails doesn't re-filter the main PDP behind it. Falls
 *   back to a document-wide `.productView` lookup when omitted or empty.
 * @param {jQuery} [$slick] The calling ProductDetails instance's thumbnail
 *   carousel (its `this.$slick`). Falls back to a document-wide
 *   `#product-details-image-carousel` lookup when omitted or empty.
 */
export default function optionImageGallery(data = {}, $scope, $slick) {
    const scope = $scope && $scope.length ? $scope : $(SCOPE_SELECTOR);
    if (!scope.length) return;

    initVideoThumb(scope);

    const slick = $slick && $slick.length ? $slick : $(SLICK_SELECTOR);

    // Gate on the THUMBNAIL CAROUSEL, not the whole .productView. The main
    // product image also renders through components/common/responsive-img
    // (product-view.html:311-319), so a product with a single image alt-tagged
    // `SKU|Descriptive alt` used to open this gate on a page where the carousel
    // <ul> was never rendered at all — there is only one image and no
    // __vimeo-embed field, so there is nothing to filter.
    if (!hasTaggedImages(slick.get(0))) return;

    // Scope marker for the thumbnail crop styling. product-view.html and
    // product-view-ssw.html now stamp this class server-side on the same
    // condition (any image alt carrying a "|" token, i.e. exactly what makes
    // hasTaggedImages() true), because a JS-added class cannot affect first
    // paint and the crop visibly changed when slick initialized. This call is
    // kept as a belt-and-braces fallback for any template that ships without
    // the server-side flag; jQuery's addClass is a no-op when the class is
    // already present, so running on top of the server-rendered class is
    // idempotent. The class is additive and permanent for the life of the page
    // - nothing here ever removes it. See FILTERED_CLASS's rule in
    // custom/pages/_product.scss for why this can't just be a global
    // .productView-thumbnails img rule: that selector is shared by six
    // storefronts and every product on them, most of which don't use this
    // feature.
    if (!slick.hasClass(FILTERED_CLASS)) slick.addClass(FILTERED_CLASS);

    const state = {
        selectedLabels: readSelectedLabels(scope.find(FORM_SELECTOR).get(0)),
        activeSku: data.sku || '',
    };

    bindSelectionRecorder(slick);

    // Read the memory BEFORE filtering: it describes the strip the shopper was
    // looking at when they clicked, which is what the count check compares.
    const carouselEl = slick.get(0);
    const memory = getGallerySelection(carouselEl);

    filterGallery(slick, state, memory);

    // Re-derive the restore target from the strip slick actually built - its
    // displayed order can differ from the pre-filter DOM order because
    // reorderCacheForVideo keeps the video second among the VISIBLE slides.
    const visibleNow = getVisibleSlides(carouselEl);
    const restoreIndex = chooseRestoreIndex(memory, visibleNow.length);
    syncMainImage(slick, scope, restoreIndex === null ? null : visibleNow[restoreIndex]);
}

const RECORDER_FLAG = 'optionImageGalleryMemoryBound';

/**
 * Remembers the position of every thumbnail the SHOPPER clicks. Programmatic
 * clicks (syncMainImage's own restore/sync triggers carry no originalEvent)
 * are ignored so an automatic fallback never overwrites the shopper's actual
 * choice - switching Maple -> Gray (4 thumbs, fallback to first) -> Black
 * (11 thumbs again) still restores the position chosen on Maple.
 *
 * The video thumbnail records nothing: its anchor has no
 * data-image-gallery-item, and opening a lightbox is a transient action, not a
 * change of which angle the main stage shows.
 */
function bindSelectionRecorder($slick) {
    if ($slick.data(RECORDER_FLAG)) return;
    $slick.data(RECORDER_FLAG, true);

    $slick.on('click', '[data-image-gallery-item]', event => {
        if (!event.originalEvent) return;

        const carouselEl = $slick.get(0);
        const slide = event.currentTarget.closest('.slick-slide');
        const visible = getVisibleSlides(carouselEl);
        const index = visible.indexOf(slide);
        if (index !== -1) rememberGallerySelection(carouselEl, index, visible.length);
    });
}

/**
 * Keeps the main stage consistent with the filtered thumbnails. Runs after
 * BigCommerce has already applied any variant image, so it only re-points the
 * main stage when the displayed image is no longer among the visible thumbs,
 * activating the first visible one in that case.
 *
 * @param {Element|null} [restoreSlide] The slide holding the shopper's
 *   remembered angle (chooseRestoreIndex resolved against the freshly filtered
 *   strip). When given and it is a photo, it is activated outright - the whole
 *   point is to OVERRIDE the variant image BigCommerce just applied with the
 *   same-position angle. A video restoreSlide falls through to the default
 *   sync: auto-opening a lightbox on option change would be hostile, so the
 *   main stage just stays consistent instead.
 */
function syncMainImage($slick, $scope, restoreSlide = null) {
    if (restoreSlide) {
        const link = restoreSlide.querySelector('[data-image-gallery-item]');
        if (link) {
            $(link).trigger('click');
            return;
        }
    }

    const $visible = $slick.find('.slick-slide:not(.slick-cloned):visible [data-image-gallery-item]');
    if (!$visible.length) return;

    const mainSrc = $scope.find('[data-image-gallery-main] img').attr('src') || '';
    const stillVisible = $visible.toArray().some(link => (
        (link.getAttribute('data-image-gallery-new-image-url') || '') === mainSrc
    ));

    if (!stillVisible) $visible.first().trigger('click');
}

const OEMBED_ENDPOINT = 'https://vimeo.com/api/oembed.json';
const CACHE_PREFIX = 'ssw:vimeoThumb:';

/**
 * Resolves the poster still for a Vimeo URL. The endpoint sends
 * cache-control: no-store, so results are cached in sessionStorage to avoid a
 * third-party request on every PDP view. Storage failures are non-fatal.
 */
export async function resolveVideoThumb(url, options = {}) {
    const {
        fetchImpl = typeof fetch === 'function' ? fetch : null,
        storage = typeof window !== 'undefined' ? window.sessionStorage : null,
    } = options;

    if (!fetchImpl) throw new Error('fetch unavailable');

    const key = `${CACHE_PREFIX}${url}`;

    try {
        const cached = storage && storage.getItem(key);
        if (cached) return cached;
    } catch (error) {
        // Storage blocked (private browsing) — fall through to the network.
    }

    const response = await fetchImpl(`${OEMBED_ENDPOINT}?url=${encodeURIComponent(url)}`);
    if (!response.ok) throw new Error(`oEmbed ${response.status}`);

    const payload = await response.json();
    if (!payload || !payload.thumbnail_url) {
        throw new Error('oEmbed response had no thumbnail_url');
    }

    try {
        if (storage) storage.setItem(key, payload.thumbnail_url);
    } catch (error) {
        // Non-fatal.
    }

    return payload.thumbnail_url;
}

const VIDEO_THUMB_SELECTOR = '[data-video-thumb]';
const MODAL_SELECTOR = '#vimeoVideoModal';
const INIT_FLAG = 'optionImageGalleryVideoBound';

/**
 * Hydrates the server-rendered video thumbnail: swaps the placeholder for the
 * real Vimeo still, and binds the lightbox. Idempotent — safe to call on every
 * option change and in the quickview modal.
 */
export function initVideoThumb($scope) {
    const $thumbs = $scope.find(VIDEO_THUMB_SELECTOR);
    if (!$thumbs.length) return;

    bindModalTeardown();

    // A product may now carry several videos - one product-level plus one per
    // option value - so each <li> is hydrated independently against its OWN
    // url. The previous single-thumb form read .data()/.attr() off the first
    // element of the collection, which would have bound every video to the
    // first one's player.
    $thumbs.each((index, element) => initOneVideoThumb($(element)));
}

function initOneVideoThumb($thumb) {
    if ($thumb.data(INIT_FLAG)) return;
    $thumb.data(INIT_FLAG, true);

    const rawUrl = $thumb.attr('data-vimeo-url');
    const parsed = parseVimeoUrl(rawUrl);

    if (!parsed) {
        console.warn('[option-image-gallery] __vimeo-embed is not a usable Vimeo URL:', rawUrl);
        // Mark and hide rather than .remove() — see isExcludedSlide for why a
        // detached <li> comes back on the next option change. The inline
        // display:none from .hide() also beats slick's
        // `.slick-initialized .slick-slide { display: block }` (0,2,0), which a
        // stylesheet rule keyed off the attribute would lose to; slick itself
        // never writes inline display on slides (slick.js:588 is the
        // slidesPerRow path, :865/:877 are the arrows in destroy()).
        // The INIT_FLAG set above survives, so this warns exactly once instead
        // of on every option change - .remove() also dropped the flag's jQuery
        // data, which is why it used to re-warn and re-remove forever.
        $thumb.attr(EXCLUDED_ATTR, '').hide();
        return;
    }

    // Placeholder stays on failure; the lightbox never depends on oEmbed.
    resolveVideoThumb(rawUrl)
        .then(thumbUrl => $thumb.find('.productView-videoThumb-image').attr('src', thumbUrl))
        .catch(error => console.warn('[option-image-gallery] Vimeo thumbnail unavailable:', error));

    $thumb.on('click', 'a', event => {
        event.preventDefault();
        openVideoLightbox(buildPlayerUrl(parsed));
    });
}

function openVideoLightbox(playerUrl) {
    const $modal = $(MODAL_SELECTOR);
    if (!$modal.length) {
        console.warn(`[option-image-gallery] ${MODAL_SELECTOR} is missing from the page`);
        return;
    }

    const separator = playerUrl.indexOf('?') === -1 ? '?' : '&';
    const iframe = document.createElement('iframe');
    iframe.src = `${playerUrl}${separator}autoplay=1`;
    iframe.className = 'vimeoModal-player';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', $modal.attr('aria-label') || 'Product video');

    $modal.find('[data-vimeo-modal-content]').empty().append(iframe);
    $modal.foundation('reveal', 'open');
}

// Destroying the iframe on close stops playback and audio.
//
// This binding MUST NOT run at module scope: the unit tests import this module
// in a plain jsdom environment with no jQuery global, so a module-level $(...)
// call would throw at import time and fail every test in Tasks 2, 3 and 6.
// It is therefore bound lazily, once, from initVideoThumb.
let modalTeardownBound = false;

function bindModalTeardown() {
    if (modalTeardownBound) return;
    modalTeardownBound = true;

    $(document).on('close.fndtn.reveal', MODAL_SELECTOR, () => {
        $(MODAL_SELECTOR).find('[data-vimeo-modal-content]').empty();
    });
}
